import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { FormCheckbox } from "../../../forms/FormCheckbox";
import { FormInput } from "../../../forms/FormInput";
import { FormPhoneNumber } from "../../../forms/FormPhoneNumber";
import { FormSelect } from "../../../forms/FormSelect";
import {
    IAddress,
    IAddressCountry,
    IAddressState,
    IBillingAddress,
    IBillingAddressErrorSet,
} from "../../../models/address";
import { wirePlacesAutocomplete } from "../../../utils/maps";
import { SavedAddressSelector } from "../SavedAddressSelector";

const parseAutocompletePlace = (
    place: google.maps.GeocoderAddressComponent[],
    currentForm: IBillingAddress,
): Partial<IBillingAddress> => {
    const placeData: { [s: string]: string } = {};
    const components: { [s: string]: "long_name" | "short_name" } = {
        street_number: "short_name",
        route: "short_name",
        postal_code: "short_name",
        locality: "long_name",
        administrative_area_level_1: "short_name",
    };

    place.forEach((component) => {
        const type = component.types[0];
        const prop = components[type] || "long_name";
        const value = component[prop];
        placeData[type] = value;
    });

    const line1 =
        (placeData.street_number || "") + " " + (placeData.route || "");
    const postcode = placeData.postal_code;
    const line4 = placeData.sublocality_level_1 || placeData.locality;
    const state = placeData.administrative_area_level_1;

    return {
        billing_line1: line1 || currentForm.billing_line1,
        billing_postcode: postcode || currentForm.billing_postcode,
        billing_line4: line4 || currentForm.billing_line4,
        billing_state: state || currentForm.billing_state,
    };
};

const getCountryOptions = (countries: readonly IAddressCountry[]) => {
    return countries.map((country) => ({
        value: country.url,
        label: country.printable_name,
    }));
};

const getStateOptions = (states: readonly IAddressState[]) => {
    const options = states.map((state) => ({
        value: state.code,
        label: state.name,
    }));
    options.unshift({
        value: "",
        label: "",
    });
    return options;
};

export const BillingAddressForm = (
    props: Immutable<{
        countries: readonly IAddressCountry[];
        states: readonly IAddressState[];
        savedAddresses: readonly IAddress[];
        form: IBillingAddress;
        errors: Partial<IBillingAddressErrorSet>;
        setBillingAddressIsSameAsShipping: (isSame: boolean) => void;
        selectSavedAddress: (
            prefix: "shipping" | "billing",
            savedAddresses: IAddress[],
            selectedAddressURL: string,
        ) => void;
        onChange: (fields: Partial<IBillingAddress>) => void;
        onValidStateChange?: (name: string, errors: string[]) => void;
        showErrorMessages?: boolean;
        isHidden?: boolean;
        disabled: boolean;
    }>,
): React.ReactNode => {
    const autocompleteRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        const elem = autocompleteRef.current;
        if (!elem) {
            return;
        }
        // The Maps library may not be loaded yet at mount (the full-document
        // checkout renders its loader async); wirePlacesAutocomplete awaits it
        // and skips the attach if this effect is cleaned up first.
        return wirePlacesAutocomplete(elem, (autocomplete) => {
            const place = autocomplete.getPlace();
            if (place.address_components) {
                const updates = parseAutocompletePlace(
                    place.address_components,
                    props.form,
                );
                props.onChange(updates);
            }
        });
    }, []);

    const onChange = (
        event:
            | React.FormEvent<HTMLInputElement>
            | React.FormEvent<HTMLSelectElement>,
    ) => {
        props.onChange({
            [event.currentTarget.name]: event.currentTarget.value,
        });
    };

    const onBillingAddrCheckboxChange = (
        event: React.FormEvent<HTMLInputElement>,
    ) => {
        props.setBillingAddressIsSameAsShipping(event.currentTarget.checked);
    };

    const countryOptions = getCountryOptions(props.countries);
    const stateOptions = getStateOptions(props.states);

    return (
        <div style={{ display: props.isHidden ? "none" : "block" }}>
            <div className="checkout-step__address-title">
                {/* TODO(i18n): Use t tagged template: {t`Billing Address`} */}
                <h3 className="checkout-step__address-title--bold">
                    Billing Address
                </h3>
                <div>
                    {t`The billing address must match the address on the credit card or bank statement.`}
                </div>
            </div>
            <FormCheckbox
                id="billing_addr_is_shipping_addr"
                name="billing_addr_is_shipping_addr"
                checked={props.form.billing_addr_is_shipping_addr}
                onChange={onBillingAddrCheckboxChange}
                label={t`Billing address is the same as shipping address`}
                disabled={props.disabled}
            />
            <div
                style={{
                    display: props.form.billing_addr_is_shipping_addr
                        ? "none"
                        : "block",
                }}
            >
                <SavedAddressSelector prefix="billing" />
                {/* TODO(i18n): Use t tagged template for label: label={t`First Name`} */}
                <FormInput
                    type="text"
                    id="billing_first_name"
                    name="billing_first_name"
                    label="First Name"
                    labelPlacement="dynamic"
                    value={props.form.billing_first_name}
                    errors={props.errors.billing_first_name}
                    onChange={onChange}
                    validation="required"
                    maxLength={25}
                    autoComplete="given-name"
                    disabled={props.disabled}
                    onValidStateChange={props.onValidStateChange}
                    showErrorMessages={props.showErrorMessages}
                />
                {/* TODO(i18n): Use t tagged template for label: label={t`Last Name`} */}
                <FormInput
                    type="text"
                    id="billing_last_name"
                    name="billing_last_name"
                    label="Last Name"
                    labelPlacement="dynamic"
                    value={props.form.billing_last_name}
                    errors={props.errors.billing_last_name}
                    onChange={onChange}
                    validation="required"
                    maxLength={25}
                    autoComplete="family-name"
                    disabled={props.disabled}
                    onValidStateChange={props.onValidStateChange}
                    showErrorMessages={props.showErrorMessages}
                />
                <FormInput
                    inputRef={autocompleteRef}
                    type="text"
                    id="billing_line1"
                    name="billing_line1"
                    label={t`Street and number, P.O. box, c/o.`}
                    labelPlacement="dynamic"
                    adaText={t`Begin typing to search, use arrow keys to navigate, Enter to select`}
                    placeholder=""
                    value={props.form.billing_line1}
                    errors={props.errors.billing_line1}
                    onChange={onChange}
                    validation="required"
                    maxLength={25}
                    maxBytes={240}
                    autoComplete="address-line1"
                    disabled={props.disabled}
                    onValidStateChange={props.onValidStateChange}
                    showErrorMessages={props.showErrorMessages}
                />
                <FormInput
                    type="text"
                    id="billing_line2"
                    name="billing_line2"
                    label={t`Apartment, suite, unit, etc. (Optional)`}
                    labelPlacement="dynamic"
                    value={props.form.billing_line2}
                    errors={props.errors.billing_line2}
                    onChange={onChange}
                    autoComplete="address-line2"
                    maxLength={25}
                    maxBytes={240}
                    disabled={props.disabled}
                    onValidStateChange={props.onValidStateChange}
                    showErrorMessages={props.showErrorMessages}
                />
                {/* TODO(i18n): Use t tagged template for label: label={t`City`} */}
                <FormInput
                    type="text"
                    id="billing_line4"
                    name="billing_line4"
                    label="City"
                    labelPlacement="dynamic"
                    value={props.form.billing_line4}
                    errors={props.errors.billing_line4}
                    onChange={onChange}
                    validation="required"
                    autoComplete="address-level2"
                    maxLength={60}
                    maxBytes={60}
                    disabled={props.disabled}
                    onValidStateChange={props.onValidStateChange}
                    showErrorMessages={props.showErrorMessages}
                />
                {/* TODO(i18n): Use t tagged template for label: label={t`State`} */}
                <FormSelect
                    label="State"
                    labelPlacement="dynamic"
                    id="billing_state"
                    name="billing_state"
                    value={props.form.billing_state}
                    errors={props.errors.billing_state}
                    onChange={onChange}
                    validation="required"
                    choices={stateOptions}
                    autoComplete="address-level1"
                    disabled={props.disabled}
                    onValidStateChange={props.onValidStateChange}
                    showErrorMessages={props.showErrorMessages}
                />
                <FormInput
                    type="text"
                    id="billing_postcode"
                    name="billing_postcode"
                    label={t`Zip Code`}
                    labelPlacement="dynamic"
                    value={props.form.billing_postcode}
                    errors={props.errors.billing_postcode}
                    onChange={onChange}
                    validation={["required", "zipcode"]}
                    autoComplete="postal-code"
                    maxLength={10}
                    maxBytes={60}
                    inputMode="numeric"
                    disabled={props.disabled}
                    onValidStateChange={props.onValidStateChange}
                    showErrorMessages={props.showErrorMessages}
                />
                <FormSelect
                    label={t`Country`}
                    labelPlacement="dynamic"
                    id="billing_country"
                    name="billing_country"
                    value={props.form.billing_country}
                    errors={props.errors.billing_country}
                    onChange={onChange}
                    validation="required"
                    choices={countryOptions}
                    autoComplete="country"
                    disabled={props.disabled}
                    onValidStateChange={props.onValidStateChange}
                    showErrorMessages={props.showErrorMessages}
                />
                <FormPhoneNumber
                    type="phone"
                    id="billing_phone_number"
                    name="billing_phone_number"
                    label={t`Phone Number`}
                    labelPlacement="dynamic"
                    value={props.form.billing_phone_number}
                    errors={props.errors.billing_phone_number}
                    onChange={onChange}
                    validation={["required", "phone"]}
                    autoComplete="tel"
                    disabled={props.disabled}
                    onValidStateChange={props.onValidStateChange}
                    showErrorMessages={props.showErrorMessages}
                />
            </div>
        </div>
    );
};
